import { TamediaStyles } from "@ta-interaktiv/microfrontend-template-helpers";
import { FC, ReactElement, useEffect, useState } from 'react'
import styled from 'styled-components'
import colorVariables from '../../Globals/colors.module.css'
import fontVariables from '../../Globals/fonts.module.css'

import { TextWidth } from '@ta-interaktiv/react-disco-grid'

import { AnnotatedMap } from 'components/AnnotatedMap'
import { GetAnnotations, GetTitle } from 'components/Localization/Annotations'
import { ChartFrame } from 'components/ChartFrame'
import { useWindowSize } from 'usehooks-ts'
import { Melting } from 'components/Melting'
import { SizeCompare } from 'components/SizeCompare'
import { Flooding } from 'components/Flooding'
import { Impressum } from 'components/Impressum'
import { Diary } from 'components/Diary'
import { TippingAnnotations } from 'components/Localization/TippingPoints'
import { TippingPointTable } from 'components/TippingPointTable'

declare global {
  interface Window {
    scrollytarget: any
  }
}

window.scrollytarget = window.scrollytarget || 0
const lang: 'de' | 'fr' = 'de'
const dev = window.document.title === 'TA-Interaktiv Microfrontend'
const MainComponent: FC<MicroFrontendProps> = ({
  tenant,
  colorMode,
  properties,
}): ReactElement => {
  const globalClasses = [
    colorVariables.globalColors,
    fontVariables.globalFonts,
    colorMode === 'dark' ? colorVariables.globalColorsDark : '',
  ]
  const visualization = properties?.visualization

  const { width, height } = useWindowSize()
  const [size, setSize] = useState({ width, height })

  useEffect(() => {
    setSize({ width, height: window.innerHeight })
  }, [])

  useEffect(() => {
    setSize({ width, height: size.height || height })
  }, [width])

  return (
    <AppContainer className={globalClasses.join(' ')}>
      {dev && <DevText />}{' '}
      {(visualization === 'tipping' || dev) && (
        <ChartFrame id='tipping'>
          <TippingPointTable lang={tenant?.lang} colorMode={colorMode} />
        </ChartFrame>
      )}
      {dev && <DevText />}{' '}
      {(visualization === 'diary' || dev) && (
        <ChartFrame id='diary'>
          <Diary lang={tenant?.lang} colorMode={colorMode} />
        </ChartFrame>
      )}
      {dev && <DevText />}{' '}
      {(visualization === 'impressum' || dev) && (
        <ChartFrame id='impressum'>
          <Impressum lang={tenant?.lang} colorMode={colorMode} />
        </ChartFrame>
      )}
      {dev && <DevText />}{' '}
      {(visualization === 'flooding' || dev) && (
        <ChartFrame id='flooding'>
          <Flooding lang={tenant?.lang} colorMode={colorMode} />
        </ChartFrame>
      )}
      {dev && <DevText />}{' '}
      {(visualization === 'size' || dev) && (
        <ChartFrame id='size'>
          <TextWidth>
            <SizeCompare lang={tenant?.lang} colorMode={colorMode} />
          </TextWidth>
        </ChartFrame>
      )}
      {dev && <DevText />}{' '}
      {(visualization === 'melting' || dev) && (
        <ChartFrame id='melting' overflowHidden>
          <TextWidth>
            <Melting lang={tenant?.lang} />
          </TextWidth>
        </ChartFrame>
      )}
      {dev && <DevText />}
      {(visualization === 'overview' || dev) && (
        <ChartFrame id='overview'>
          <AnnotatedMap
            image={require('../Assets/overview.png')}
            annotations={GetAnnotations(tenant?.lang || 'de', 'overview')}
            screenHeight
            windowSize={size}
            source={
              (tenant?.lang === 'fr' ? 'Source: ' : 'Quelle: ') +
              'Radarsat Antarctic Mapping Project (RAMP) 2013, International Bathymetric Chart of the Southern Ocean v2 (IBCSO) 2022'
            }
          />
        </ChartFrame>
      )}
      {dev && <DevText />}
      {(visualization === 'landmass' || dev) && (
        <ChartFrame id='landmass'>
          <TextWidth>
            <div className='title' style={{ marginBottom: '15px' }}>
              {GetTitle(tenant?.lang || 'de', 'B')}
            </div>
            <AnnoColorScale>
              <div style={{ left: '50%',textAlign:"center" }}>0</div>
              <div style={{ left: '10px' }}>-5000m</div>
              <div
                style={{
                  left: 'calc(100% - 10px)',
                  transform: 'translateX(-100%)',
                }}
              >
                5000m
              </div>
              <img src={require('../Assets/landwasser_colorscale.jpg')} />
            </AnnoColorScale>
          </TextWidth>
          <AnnotatedMap
            image={require('../Assets/landwasser.png')}
            annotations={GetAnnotations(tenant?.lang || 'de', 'landmass')}
            screenHeight
            windowSize={size}
            // source={
            //   (tenant?.lang === 'fr' ? 'Source: ' : 'Quelle: ') +
            //   'International Bathymetric Chart of the Southern Ocean v2 (IBCSO) 2022'
            // }
          />
          <TextWidth>
            <Subline>
              {tenant?.lang === 'fr' ? (
                <>
                  Une grande partie de la masse terrestre de l'Antarctique
                  occidental se trouve sous le niveau de la mer.
                </>
              ) : (
                <>
                  Grosse Teile der west&shy;antarktischen Land&shy;masse liegen
                  unterhalb des Meeres&shy;spiegels.
                </>
              )}
            </Subline>
            <div className='source'>
              {(tenant?.lang === 'fr' ? 'Source: ' : 'Quelle: ') +
                'International Bathymetric Chart of the Southern Ocean v2 (IBCSO) 2022'}
            </div>
          </TextWidth>
        </ChartFrame>
      )}
      {dev && <DevText />}
    </AppContainer>
  )
}
const AppContainer = styled(TamediaStyles)`
  margin: 0;
  padding: 0;
  font-family: var(--font-serif);
  font-size: 1.8rem;
  line-height: 3rem;
  max-width: 100%;
  #back {
    overflow-x: hidden;
  }
`
export default MainComponent

const DevText = () => (
  <TextWidth>
    Der Grund, dass das Wasser unter dem Thwaites-Gletscher eine derart
    verheerende Wirkung hat, liegt an zwei Besonderheiten. Erstens befinden sich
    weite Teile des westantarktischen Eises unter dem Meeresspiegel – das Eis
    liegt mit der Unterseite also auf dem Meeresboden auf. Deshalb spricht man
    hier genaugenommen auch nicht nur von einem Eisschild, wie wir weiter oben
    behauptet haben. Man spricht von einem marinen Eisschild.46 Das heisst,
    schmilzt dieses Eis dereinst komplett ab, bleibt als Landfläche ausser ein
    paar Inseln nichts zurück.
  </TextWidth>
)

const Subline = styled.div`
  font-size: 15px;
  font-weight: 700;
  line-height: 1.3em;
  max-width: 600px;
  margin-bottom: 5px;
`
const AnnoColorScale = styled.div`
  font-size: 13px;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
  height: 60px;
  position: relative;

  div {
    position: absolute;
    top: 0;
  }
  text-align: center;
  img {
    left: 50%;
    transform: translateX(-50%);
    position: absolute;
    bottom: 10px;
  }
`
